import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router';

import { AppLayout } from '../../components';
import { PATHS } from '../../routes/paths';
import { useRequiredSession } from '../../session';
import { useCompanies, useRememberSelectedCompany } from '../queries';
import {
  CompanyScopeContext,
  entitledCompanies,
  initialCompany,
  useCompanyScope,
  type CompanyScope,
} from './companyScope';

/**
 * Addresses that name a record of one company. Switching away from such a page
 * would leave it asking the new company for somebody else's record, so the
 * switch goes to the portal home instead of to a "not found".
 */
const COMPANY_RECORD_PATHS: readonly string[] = [PATHS.customerPerson];

/**
 * The provider and the switcher. The rules they enforce are documented in
 * `companyScope.ts`, beside the context they put on the tree.
 *
 * The provider also draws the portal's frame, because the frame shows which
 * company this is and carries the switcher: every customer page gets both
 * without asking for them.
 */
export function CompanyScopeProvider({ children }: { children: ReactNode }) {
  const session = useRequiredSession();
  const principal = session.principal;
  const companiesQuery = useCompanies();
  const remember = useRememberSelectedCompany();
  const navigate = useNavigate();
  const location = useLocation();
  const [chosenId, setChosenId] = useState<string | null>(null);

  // The list call is the authority once it has answered; until then the
  // principal's own `companies`, which the server sent with the session.
  const companies = useMemo(
    () =>
      entitledCompanies(
        principal.company,
        companiesQuery.data?.items ?? principal.companies ?? undefined,
      ),
    [principal.company, principal.companies, companiesQuery.data],
  );

  // A choice that is no longer entitled is not honoured -- it falls back to the
  // remembered company or the principal's own rather than being carried, so a
  // revoked entitlement cannot leave a stale company id going out on the wire.
  const selected =
    companies.find((company) => company.id === chosenId) ??
    initialCompany(companies, principal.selected_company, principal.company);

  if (selected === undefined) {
    return (
      <AppLayout area="customer">
        <div className="page">
          <div className="state state--empty">
            <p className="state__title">Nenhuma empresa vinculada a este acesso</p>
            <p className="state__message">
              Este usuário não está ligado a nenhuma empresa cliente. Fale com a vyttech para
              regularizar o acesso.
            </p>
          </div>
        </div>
      </AppLayout>
    );
  }

  const selectCompany = (companyId: string) => {
    if (companyId === selected.id) {
      return;
    }
    setChosenId(companyId);
    // Remembered for the next session, and nothing waits on it: a failure
    // costs the person the default next time, not the switch they just made.
    remember.mutate(companyId);
    if (COMPANY_RECORD_PATHS.some((path) => matchPath(path, location.pathname) !== null)) {
      void navigate(PATHS.customer);
    }
  };

  const scope: CompanyScope = {
    company: selected,
    companies,
    selectCompany,
    headers: { 'X-Company-Id': selected.id },
  };

  return (
    <CompanyScopeContext value={scope}>
      <AppLayout
        area="customer"
        accountDetail={
          principal.group !== null && principal.group !== undefined
            ? `${selected.name} · ${principal.group.name}`
            : selected.name
        }
        toolbar={<CompanySwitcher />}
      >
        {/* Keyed by the company, so a switch mounts every screen afresh: no
            half-typed form, upload preview or other local state of the previous
            company survives into the next one. Cached queries cannot leak
            either -- every portal query key names its company. */}
        <Fragment key={selected.id}>{children}</Fragment>
      </AppLayout>
    </CompanyScopeContext>
  );
}

/**
 * The switcher, shown only when there is something to switch between.
 *
 * A select with one option is a control that cannot do anything, and a portal
 * that shows one to every single-company user is a portal that looks like it
 * has a setting nobody understands.
 */
export function CompanySwitcher() {
  const { company, companies, selectCompany } = useCompanyScope();

  if (companies.length < 2) {
    return null;
  }

  return (
    <div className="company-switcher">
      <label className="field__label" htmlFor="empresa-selecionada">
        Empresa
      </label>
      <select
        id="empresa-selecionada"
        className="field__input"
        value={company.id}
        onChange={(event) => selectCompany(event.target.value)}
      >
        {companies.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>
    </div>
  );
}
